import React, { useEffect, useRef, useState } from "react";
import { Loader2, CheckCircle2, Upload, User } from "lucide-react";
import { storage } from "../../lib/firebase";
import type { VCard, MessagingApp, MessagingContact } from "../../types/vcard";
import { useVCardProfileSync } from "../../hooks/useVCardProfileSync";
import useUniversalToggleWithAddress from "../../hooks/useUniversalToggleWithAddress";
import {
  buildProtectedUploadFileName,
  uploadProtectedImage,
} from "../../services/storage/malwareScan";
import { optimizeProtectedImage } from "../../services/storage/imageOptimization";
import {
  COUNTRY_CODE_OPTIONS,
  DEFAULT_COUNTRY_CODE,
} from "../../constants/countryCodes";
import {
  detectPhoneCountryCode,
  formatPhoneBodyForDisplay,
  normalizePhoneForStorage,
  stripCountryCodeForDisplay,
} from "../../utils/phone";
import CountryCodeSelect from "../ui/CountryCodeSelect";
import { useUserStore } from "../../store/userStore";
import type { UserProfile } from "../../types/user";

const MESSAGING_APPS: MessagingApp[] = [
  "WhatsApp",
  "iMessage",
  "Messenger",
  "Telegram",
  "Snapchat",
  "WeChat",
  "Line",
];

type MessagingHandleMap = Partial<Record<MessagingApp, string>>;
type BusinessCategoryData = {
  company?: string;
  title?: string;
  department?: string;
  linkedin?: string;
  logoUrl?: string;
  tagline?: string;
  brochureUrl?: string;
  assistant?: {
    name?: string;
    email?: string;
    phone?: string;
  };
  digitalCalendar?: {
    type?: string;
    url?: string;
  };
};

const normalizeMessagingContactsFromMap = (
  handlesByApp: MessagingHandleMap,
): MessagingContact[] =>
  MESSAGING_APPS.map((app) => ({
    app,
    handle: (handlesByApp[app] || "").trim(),
  })).filter((contact) => Boolean(contact.handle));

const getMessagingState = (source: {
  messagingContact?: MessagingContact | null;
  messagingContacts?: MessagingContact[];
}): { selectedApp: MessagingApp | ""; handlesByApp: MessagingHandleMap } => {
  const sourceContacts = Array.isArray(source.messagingContacts)
    ? source.messagingContacts
    : source.messagingContact
      ? [source.messagingContact]
      : [];

  const handlesByApp: MessagingHandleMap = {};
  sourceContacts.forEach((contact) => {
    if (contact?.app) {
      handlesByApp[contact.app] = contact.handle ?? "";
    }
  });

  const selectedApp =
    source.messagingContact?.app &&
    MESSAGING_APPS.includes(source.messagingContact.app)
      ? source.messagingContact.app
      : sourceContacts[0]?.app || "";

  return {
    selectedApp,
    handlesByApp,
  };
};

const getSelectedHandle = (
  selectedApp: MessagingApp | "",
  handlesByApp: MessagingHandleMap,
): string => {
  if (!selectedApp) {
    return "";
  }

  return handlesByApp[selectedApp] || "";
};

interface BusinessInfoProps {
  formData: Partial<VCard>;
  handleChange: (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >,
  ) => void;
  handleSubmit: (e: React.FormEvent) => Promise<void>;
  isSubmitting?: boolean;
  showSaveConfirmation?: boolean;
  isActive: boolean;
}

const BusinessInfo: React.FC<BusinessInfoProps> = ({
  formData,
  handleChange,
  handleSubmit,
  isSubmitting = false,
  showSaveConfirmation = false,
  isActive,
}) => {
  void isActive;
  const [workPhoneCountryCode, setWorkPhoneCountryCode] =
    useState(DEFAULT_COUNTRY_CODE);
  const [mobilePhoneCountryCode, setMobilePhoneCountryCode] =
    useState(DEFAULT_COUNTRY_CODE);
  const [travelPhoneCountryCode, setTravelPhoneCountryCode] =
    useState(DEFAULT_COUNTRY_CODE);
  const [isUploadingPhoto, setIsUploadingPhoto] = useState(false);
  const [photoUploadError, setPhotoUploadError] = useState<string | null>(null);
  const photoInputRef = useRef<HTMLInputElement>(null);
  const [selectedMessagingApp, setSelectedMessagingApp] = useState<
    MessagingApp | ""
  >("");
  const [messagingHandlesByApp, setMessagingHandlesByApp] =
    useState<MessagingHandleMap>({});
  const profile = useUserStore((state) => state.profile);
  const updateProfile = useUserStore((state) => state.updateProfile);

  // Use the vCard profile sync hook
  const { vcard: businessVCard } = useVCardProfileSync("business", {
    syncFields: [
      "firstName",
      "lastName",
      "email",
      "workEmail",
      "mobilePhone",
      "travelPhone",
      "workPhone",
      "messagingContact",
      "messagingContacts",
      "company",
      "jobTitle",
      "categorySpecificInfo.business.linkedin",
      "isActive",
    ],
    debounceMs: 300,
  });

  // Initialize our universal toggle hook for address update
  const {
    universalToggleWithAddress,
    isLoading: toggleLoading,
    error: toggleError,
  } = useUniversalToggleWithAddress();

  // Handler for toggling business active state
  const handleToggle = async () => {
    try {
      await universalToggleWithAddress(businessVCard?.id || "business");
    } catch (err) {
      console.error(err);
    }
  };

  // Use synced data if available
  const syncedFormData = {
    ...formData,
    firstName: formData.firstName ?? businessVCard?.firstName,
    lastName: formData.lastName ?? businessVCard?.lastName,
    email: formData.email ?? businessVCard?.email,
    workEmail: formData.workEmail ?? businessVCard?.workEmail,
    mobilePhone: formData.mobilePhone ?? businessVCard?.mobilePhone,
    travelPhone: formData.travelPhone ?? businessVCard?.travelPhone,
    workPhone: formData.workPhone ?? businessVCard?.workPhone,
    messagingContacts: Array.isArray(formData.messagingContacts)
      ? formData.messagingContacts
      : Array.isArray(businessVCard?.messagingContacts)
        ? businessVCard.messagingContacts
        : Array.isArray(profile?.messagingContacts)
          ? profile.messagingContacts
          : [],
    messagingContact:
      formData.messagingContact ||
      businessVCard?.messagingContact ||
      profile?.messagingContact ||
      formData.messagingContacts?.[0] ||
      businessVCard?.messagingContacts?.[0] ||
      profile?.messagingContacts?.[0],
    company: formData.company ?? businessVCard?.company,
    jobTitle: formData.jobTitle ?? businessVCard?.jobTitle,
    isActive: formData.isActive ?? businessVCard?.isActive ?? false,
  };
  const businessCategoryData: BusinessCategoryData = (() => {
    const categorySpecificInfo = syncedFormData.categorySpecificInfo;
    if (!categorySpecificInfo || typeof categorySpecificInfo !== "object") {
      return {};
    }
    const maybeBusiness = (categorySpecificInfo as Record<string, unknown>)
      .business;
    if (!maybeBusiness || typeof maybeBusiness !== "object") {
      return {};
    }

    return maybeBusiness as BusinessCategoryData;
  })();
  const syncedCompany =
    businessCategoryData.company || syncedFormData.company || "";
  const syncedJobTitle =
    businessCategoryData.title || syncedFormData.jobTitle || "";
  const syncedLinkedIn =
    businessCategoryData.linkedin || profile?.socialMedia?.linkedin || "";

  const syncMessagingFields = (
    nextSelectedApp: MessagingApp | "",
    nextHandlesByApp: MessagingHandleMap,
  ) => {
    const normalizedContacts =
      normalizeMessagingContactsFromMap(nextHandlesByApp);
    const selectedHandle = nextSelectedApp
      ? (nextHandlesByApp[nextSelectedApp] || "").trim()
      : "";

    const primaryContact: MessagingContact | null = nextSelectedApp
      ? {
          app: nextSelectedApp,
          handle: selectedHandle,
        }
      : (normalizedContacts[0] ?? null);

    handleChange({
      target: {
        name: "messagingContacts",
        value: normalizedContacts,
      },
    } as unknown as React.ChangeEvent<HTMLInputElement>);

    handleChange({
      target: {
        name: "messagingContact",
        value: primaryContact,
      },
    } as unknown as React.ChangeEvent<HTMLInputElement>);
  };

  useEffect(() => {
    if (syncedFormData.mobilePhone?.startsWith("+")) {
      const code = detectPhoneCountryCode(
        syncedFormData.mobilePhone,
        COUNTRY_CODE_OPTIONS,
        DEFAULT_COUNTRY_CODE,
      );
      setMobilePhoneCountryCode(code);
    }
    if (syncedFormData.workPhone?.startsWith("+")) {
      const code = detectPhoneCountryCode(
        syncedFormData.workPhone,
        COUNTRY_CODE_OPTIONS,
        DEFAULT_COUNTRY_CODE,
      );
      setWorkPhoneCountryCode(code);
    }
    if (syncedFormData.travelPhone?.startsWith("+")) {
      const code = detectPhoneCountryCode(
        syncedFormData.travelPhone,
        COUNTRY_CODE_OPTIONS,
        DEFAULT_COUNTRY_CODE,
      );
      setTravelPhoneCountryCode(code);
    }

    const messagingState = getMessagingState({
      messagingContact: syncedFormData.messagingContact || null,
      messagingContacts: syncedFormData.messagingContacts,
    });
    setSelectedMessagingApp(messagingState.selectedApp);
    setMessagingHandlesByApp(messagingState.handlesByApp);
  }, [
    syncedFormData.mobilePhone,
    syncedFormData.workPhone,
    syncedFormData.travelPhone,
    syncedFormData.messagingContact,
    syncedFormData.messagingContacts,
  ]);

  useEffect(() => {
    const nextUpdates: Array<{ name: string; value: string }> = [];
    const companyFromNested = (businessCategoryData.company || "").trim();
    const companyFromLegacy = (syncedFormData.company || "").trim();
    const titleFromNested = (businessCategoryData.title || "").trim();
    const titleFromLegacy = (syncedFormData.jobTitle || "").trim();

    if (!companyFromNested && companyFromLegacy) {
      nextUpdates.push({
        name: "categorySpecificInfo.business.company",
        value: companyFromLegacy,
      });
    }

    if (!titleFromNested && titleFromLegacy) {
      nextUpdates.push({
        name: "categorySpecificInfo.business.title",
        value: titleFromLegacy,
      });
    }

    nextUpdates.forEach((update) => {
      handleChange({
        target: update,
      } as unknown as React.ChangeEvent<HTMLInputElement>);
    });
  }, [
    businessCategoryData.company,
    businessCategoryData.title,
    handleChange,
    syncedFormData.company,
    syncedFormData.jobTitle,
  ]);

  useEffect(() => {
    const businessLinkedIn = (businessCategoryData.linkedin || "").trim();
    const profileLinkedIn = (profile?.socialMedia?.linkedin || "").trim();

    if (!businessLinkedIn && profileLinkedIn) {
      handleChange({
        target: {
          name: "categorySpecificInfo.business.linkedin",
          value: profileLinkedIn,
        },
      } as unknown as React.ChangeEvent<HTMLInputElement>);
    }
  }, [
    businessCategoryData.linkedin,
    handleChange,
    profile?.socialMedia?.linkedin,
  ]);

  const handleHeadshotUpload = async (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = e.target.files?.[0];
    if (!file || !profile) return;
    setIsUploadingPhoto(true);
    setPhotoUploadError(null);
    try {
      const optimized = await optimizeProtectedImage(file);
      const uploadFileName = buildProtectedUploadFileName(
        optimized.name,
        "headshot",
      );
      const { downloadUrl } = await uploadProtectedImage({
        storage,
        storagePath: `vcard-photos/${profile.id}/business/${uploadFileName}`,
        file: optimized,
      });
      handleChange({
        target: { name: "photo", value: downloadUrl },
      } as React.ChangeEvent<HTMLInputElement>);
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : "Upload failed. Please try again.";
      setPhotoUploadError(message);
    } finally {
      setIsUploadingPhoto(false);
      // Reset so the same file can be re-selected if needed
      if (photoInputRef.current) photoInputRef.current.value = "";
    }
  };

  const handleBusinessSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const formDataSnapshot = new FormData(e.currentTarget as HTMLFormElement);
    await handleSubmit(e);

    if (!profile) {
      return;
    }

    const profileUpdates: Partial<UserProfile> = {};
    const formFirstName = (formDataSnapshot.get("firstName") as string) || "";
    const formLastName = (formDataSnapshot.get("lastName") as string) || "";
    const formWorkEmail = (formDataSnapshot.get("workEmail") as string) || "";
    const formMobilePhone =
      (formDataSnapshot.get("mobilePhone") as string) || "";
    const formWorkPhone = (formDataSnapshot.get("workPhone") as string) || "";
    const formTravelPhone =
      (formDataSnapshot.get("travelPhone") as string) || "";
    const formLinkedIn =
      (formDataSnapshot.get(
        "categorySpecificInfo.business.linkedin",
      ) as string) || "";

    if (formFirstName && formFirstName !== profile.firstName) {
      profileUpdates.firstName = formFirstName;
    }

    if (formLastName && formLastName !== profile.lastName) {
      profileUpdates.lastName = formLastName;
    }

    if (formWorkEmail !== (profile.alternativeEmail || "")) {
      profileUpdates.alternativeEmail = formWorkEmail;
    }

    if (formMobilePhone && formMobilePhone !== profile.phone) {
      profileUpdates.phone = formMobilePhone;
    }

    if (formWorkPhone !== (profile.workPhone || "")) {
      profileUpdates.workPhone = formWorkPhone;
    }

    if (formTravelPhone !== (profile.travelPhone || "")) {
      profileUpdates.travelPhone = formTravelPhone;
    }

    if (formLinkedIn !== (profile.socialMedia?.linkedin || "")) {
      profileUpdates.socialMedia = {
        ...(profile.socialMedia || {}),
        linkedin: formLinkedIn,
      };
    }

    const normalizeMessagingContacts = (
      contacts: Array<MessagingContact | null | undefined>,
    ): MessagingContact[] =>
      contacts
        .filter((contact): contact is MessagingContact =>
          Boolean(contact && contact.app),
        )
        .map((contact) => ({
          app: contact.app,
          handle: contact.handle || "",
        }));

    const submittedMessagingContacts = normalizeMessagingContacts(
      Array.isArray(formData.messagingContacts)
        ? formData.messagingContacts
        : formData.messagingContact
          ? [formData.messagingContact]
          : [],
    );
    const currentMessagingContacts = normalizeMessagingContacts(
      Array.isArray(profile.messagingContacts)
        ? profile.messagingContacts
        : profile.messagingContact
          ? [profile.messagingContact]
          : [],
    );
    const submittedPrimaryMessagingContact = formData.messagingContact?.app
      ? {
          app: formData.messagingContact.app,
          handle: formData.messagingContact.handle || "",
        }
      : submittedMessagingContacts[0] || null;
    const currentPrimaryMessagingContact = profile.messagingContact?.app
      ? {
          app: profile.messagingContact.app,
          handle: profile.messagingContact.handle || "",
        }
      : currentMessagingContacts[0] || null;
    if (
      JSON.stringify(submittedMessagingContacts) !==
        JSON.stringify(currentMessagingContacts) ||
      JSON.stringify(submittedPrimaryMessagingContact) !==
        JSON.stringify(currentPrimaryMessagingContact)
    ) {
      profileUpdates.messagingContacts = submittedMessagingContacts;
      profileUpdates.messagingContact = submittedPrimaryMessagingContact;
    }

    if (Object.keys(profileUpdates).length > 0) {
      await updateProfile(profileUpdates);
    }
  };

  return (
    <form
      onSubmit={(e) => {
        void handleBusinessSubmit(e);
      }}
      className="space-y-6"
    >
      {/* Active/Inactive Toggle */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-2">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            Business Card Tag
          </h2>
          <span className="text-sm text-gray-500 dark:text-gray-400">
            (Defaults to Home when not Active)
          </span>
        </div>
        <label className="relative inline-flex items-center cursor-pointer">
          <input
            type="checkbox"
            className="sr-only peer"
            checked={syncedFormData.isActive}
            onChange={() => {
              void handleToggle();
            }}
          />
          <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-green-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-600"></div>
          <span className="ml-2 text-sm font-medium text-gray-900">
            {syncedFormData.isActive ? (
              <span className="text-green-600">Active</span>
            ) : (
              <span className="text-orange-500">Inactive</span>
            )}
          </span>
        </label>
      </div>

      {showSaveConfirmation && (
        <div className="flex items-center space-x-2 p-4 bg-green-50 text-green-700 rounded-lg">
          <CheckCircle2 className="h-5 w-5" />
          <span>Changes saved successfully!</span>
        </div>
      )}

      {/* Professional Headshot */}
      <div>
        <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">
          Professional Headshot
        </h3>
        <div className="flex items-center gap-5">
          {/* Circular preview */}
          <button
            type="button"
            onClick={() => {
              setPhotoUploadError(null);
              photoInputRef.current?.click();
            }}
            className="relative w-24 h-24 rounded-full overflow-hidden shrink-0 group focus:outline-none focus:ring-2 focus:ring-red-500"
          >
            {syncedFormData.photo ? (
              <img
                src={syncedFormData.photo}
                alt="Headshot preview"
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full bg-gray-100 flex items-center justify-center">
                <User className="h-10 w-10 text-gray-400" />
              </div>
            )}
            <div className="absolute inset-0 bg-black/50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
              {isUploadingPhoto ? (
                <Loader2 className="h-6 w-6 text-white animate-spin" />
              ) : (
                <Upload className="h-6 w-6 text-white" />
              )}
            </div>
          </button>

          <div>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
              Appears on your business card only. Recommended: square, 512×512px
              or larger.
            </p>
            <button
              type="button"
              onClick={() => {
                setPhotoUploadError(null);
                photoInputRef.current?.click();
              }}
              disabled={isUploadingPhoto}
              className="text-sm font-medium text-red-600 hover:text-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isUploadingPhoto
                ? "Uploading…"
                : syncedFormData.photo
                  ? "Change Photo"
                  : "Upload Photo"}
            </button>
            {photoUploadError && (
              <p
                role="alert"
                aria-live="polite"
                className="mt-1 text-xs text-red-600"
              >
                {photoUploadError}
              </p>
            )}
          </div>

          <input
            ref={photoInputRef}
            type="file"
            accept="image/*"
            title="Upload professional headshot"
            aria-label="Upload professional headshot"
            className="hidden"
            onChange={(e) => {
              void handleHeadshotUpload(e);
            }}
          />
        </div>
      </div>

      {/* Personal Information */}
      <div>
        <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">
          Personal Information
        </h3>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              First Name*
            </label>
            <input
              type="text"
              name="firstName"
              value={syncedFormData.firstName || ""}
              onChange={handleChange}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Last Name*
            </label>
            <input
              type="text"
              name="lastName"
              value={syncedFormData.lastName || ""}
              onChange={handleChange}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
            />
          </div>
        </div>
      </div>

      {/* Business Information */}
      <div>
        <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">
          Business Information
        </h3>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Company Name*
            </label>
            <input
              type="text"
              name="categorySpecificInfo.business.company"
              value={syncedCompany}
              onChange={handleChange}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Job Title*
            </label>
            <input
              type="text"
              name="categorySpecificInfo.business.title"
              value={syncedJobTitle}
              onChange={handleChange}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Department
            </label>
            <input
              type="text"
              name="categorySpecificInfo.business.department"
              value={businessCategoryData.department || ""}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              LinkedIn
            </label>
            <input
              type="url"
              name="categorySpecificInfo.business.linkedin"
              value={syncedLinkedIn}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
              placeholder="https://linkedin.com/in/your-profile"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Company Logo URL
            </label>
            <input
              type="url"
              name="categorySpecificInfo.business.logoUrl"
              value={businessCategoryData.logoUrl || ""}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
              placeholder="https://example.com/logo.png"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Company Tagline
            </label>
            <input
              type="text"
              name="categorySpecificInfo.business.tagline"
              value={businessCategoryData.tagline || ""}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
              placeholder="e.g. Sustainable Environmental Solutions"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Brochure URL
            </label>
            <input
              type="url"
              name="categorySpecificInfo.business.brochureUrl"
              value={businessCategoryData.brochureUrl || ""}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
              placeholder="https://example.com/brochure.pdf"
            />
          </div>
        </div>
      </div>

      {/* Contact Information */}
      <div>
        <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">
          Contact Information
        </h3>
        <div className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="sm:col-span-2">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Work Email*
              </label>
              <input
                type="email"
                name="workEmail"
                value={syncedFormData.workEmail || ""}
                onChange={handleChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 text-sm"
              />
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Mobile Phone
              </label>
              <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                <CountryCodeSelect
                  value={mobilePhoneCountryCode}
                  onChange={setMobilePhoneCountryCode}
                  className="w-full sm:w-24"
                  buttonClassName="h-[41px]"
                />
                <input
                  type="tel"
                  name="mobilePhone"
                  value={formatPhoneBodyForDisplay(
                    stripCountryCodeForDisplay(
                      syncedFormData.mobilePhone,
                      mobilePhoneCountryCode,
                    ),
                  )}
                  onChange={(e) => {
                    handleChange({
                      target: {
                        name: "mobilePhone",
                        value: normalizePhoneForStorage(
                          e.target.value,
                          mobilePhoneCountryCode,
                        ),
                      },
                    } as React.ChangeEvent<HTMLInputElement>);
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 text-sm"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Work Phone*
              </label>
              <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                <CountryCodeSelect
                  value={workPhoneCountryCode}
                  onChange={setWorkPhoneCountryCode}
                  className="w-full sm:w-24"
                  buttonClassName="h-[41px]"
                />
                <input
                  type="tel"
                  name="workPhone"
                  value={formatPhoneBodyForDisplay(
                    stripCountryCodeForDisplay(
                      syncedFormData.workPhone,
                      workPhoneCountryCode,
                    ),
                  )}
                  onChange={(e) => {
                    handleChange({
                      target: {
                        name: "workPhone",
                        value: normalizePhoneForStorage(
                          e.target.value,
                          workPhoneCountryCode,
                        ),
                      },
                    } as React.ChangeEvent<HTMLInputElement>);
                  }}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 text-sm"
                />
              </div>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Travel Phone Number
              </label>
              <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                <CountryCodeSelect
                  value={travelPhoneCountryCode}
                  onChange={setTravelPhoneCountryCode}
                  className="w-full sm:w-24"
                  buttonClassName="h-[41px]"
                />
                <input
                  type="tel"
                  name="travelPhone"
                  value={formatPhoneBodyForDisplay(
                    stripCountryCodeForDisplay(
                      syncedFormData.travelPhone,
                      travelPhoneCountryCode,
                    ),
                  )}
                  onChange={(e) => {
                    handleChange({
                      target: {
                        name: "travelPhone",
                        value: normalizePhoneForStorage(
                          e.target.value,
                          travelPhoneCountryCode,
                        ),
                      },
                    } as React.ChangeEvent<HTMLInputElement>);
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 text-sm"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Messaging App
              </label>
              <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                <select
                  value={selectedMessagingApp}
                  onChange={(e) => {
                    const nextApp = e.target.value as MessagingApp | "";
                    setSelectedMessagingApp(nextApp);
                    syncMessagingFields(nextApp, messagingHandlesByApp);
                  }}
                  className="w-full sm:w-[calc(7.65rem+0.5ch)] sm:shrink-0 px-3 pr-9 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 text-sm"
                >
                  <option value="">Select</option>
                  {MESSAGING_APPS.map((app) => (
                    <option key={app} value={app}>
                      {app}
                    </option>
                  ))}
                </select>
                {selectedMessagingApp ? (
                  <input
                    type="text"
                    value={getSelectedHandle(
                      selectedMessagingApp,
                      messagingHandlesByApp,
                    )}
                    onChange={(e) => {
                      const nextHandlesByApp = {
                        ...messagingHandlesByApp,
                        [selectedMessagingApp]: e.target.value,
                      };
                      setMessagingHandlesByApp(nextHandlesByApp);
                      syncMessagingFields(
                        selectedMessagingApp,
                        nextHandlesByApp,
                      );
                    }}
                    placeholder={`Enter ${selectedMessagingApp} handle`}
                    className="w-full sm:flex-1 sm:min-w-0 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 text-sm"
                  />
                ) : (
                  <div className="w-full sm:flex-1 sm:min-w-0 px-3 py-2 border border-gray-200 rounded-lg bg-gray-100 text-gray-400 text-sm">
                    Select app first
                  </div>
                )}
              </div>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Website
            </label>
            <input
              type="url"
              name="website"
              value={syncedFormData.website || ""}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
              placeholder="https://example.com"
            />
          </div>
        </div>
      </div>

      {/* Assistant Information */}
      <div>
        <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">
          Assistant Information
        </h3>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Assistant Name
            </label>
            <input
              type="text"
              name="categorySpecificInfo.business.assistant.name"
              value={businessCategoryData.assistant?.name || ""}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Assistant Email
              </label>
              <input
                type="email"
                name="categorySpecificInfo.business.assistant.email"
                value={businessCategoryData.assistant?.email || ""}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Assistant Phone
              </label>
              <input
                type="tel"
                name="categorySpecificInfo.business.assistant.phone"
                value={businessCategoryData.assistant?.phone || ""}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Digital Calendar */}
      <div>
        <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">
          Digital Calendar
        </h3>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Calendar Type
            </label>
            <select
              name="categorySpecificInfo.business.digitalCalendar.type"
              value={businessCategoryData.digitalCalendar?.type || ""}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
            >
              <option value="">Select calendar type</option>
              <option value="calendly">Calendly</option>
              <option value="calcom">Cal.com</option>
              <option value="microsoft">Microsoft Bookings</option>
              <option value="google">Google Calendar</option>
              <option value="custom">Custom</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Calendar URL
            </label>
            <input
              type="url"
              name="categorySpecificInfo.business.digitalCalendar.url"
              value={businessCategoryData.digitalCalendar?.url || ""}
              onChange={handleChange}
              placeholder="https://calendly.com/your-username"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
            />
          </div>
        </div>
      </div>

      {/* Save Button */}
      <button
        type="submit"
        disabled={isSubmitting || toggleLoading}
        className="w-full bg-red-600 text-white py-2 px-4 rounded-lg hover:bg-red-700 transition-colors disabled:bg-red-400 disabled:cursor-not-allowed"
      >
        {isSubmitting || toggleLoading ? (
          <div className="flex items-center justify-center">
            <Loader2 className="h-5 w-5 animate-spin mr-2" />
            <span>Saving...</span>
          </div>
        ) : (
          "Save Business Card"
        )}
      </button>

      {toggleError && (
        <p className="mt-4 text-red-600">Toggle Error: {toggleError}</p>
      )}
    </form>
  );
};

export default BusinessInfo;
